import {
  Box,
  Typography,
  Stack,
  Select,
  ListSubheader,
  MenuItem,
  Checkbox,
  OutlinedInput,
  SelectChangeEvent,
  Divider,
  Button,
  Link,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { DbMarkerCategory } from "../../../Core/Api/Types/db-marker-category";
import { DbMarkerSeverity } from "../../../Core/Api/Types/db-marker-severity";
import { cacheService } from "../../../Core/Api/services";
import { FilterList, PushPinOutlined, Search } from "@mui/icons-material";
import QCMarkerCard, { QCMarkerCardProps } from "../../Cards/QCMarkerCard/qc-marker-card";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { useDispatch } from "react-redux";
import {
  GetFrameRate,
  GetIsFileTCDisplayed,
  GetTimecode,
  GetTimecodeIn,
} from "../../../Redux/Reducers/redux-slice-react-player";
import { TutorialContext } from "../../../Utils/Helpers/tutorial";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { useAppSelector } from "../../../Redux/redux-store";
import {
  GetCreatingOrEditingTimelineEvent,
  GetFilteredTimelineMarkers,
  GetTimelineMarkers,
  SetActiveTimeline,
  SetCreatingOrEditingTimelineEvent,
  SetFilteredTimelineMarkers,
  SetTimelineMarkers,
  TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import { useSelector } from "react-redux";
import EmptyListSlot from "../../Misc/EmptyListSlot";
import { IPreviewTabContentProps } from "./preview-tabs";
import { useTranslation } from "react-i18next";
import { FrameToTimecode, TimecodeToFileTimecode } from "../../../Utils/Timecode/ReactPlayer";

export const MarkersTab: React.FC<IPreviewTabContentProps> = (props: IPreviewTabContentProps) => {
  const { t } = useTranslation();
  // Props & Hooks
  const { asset } = props;
  const dispatch = useDispatch();
  const tutorialContext = React.useContext(TutorialContext);
  const { setTutorialPage } = { ...tutorialContext };

  const markers = useSelector(GetTimelineMarkers);
  const filteredMarkers = useSelector(GetFilteredTimelineMarkers);
  const frameRate = useSelector(GetFrameRate);
  const timecodeIn = useSelector(GetTimecodeIn);

  const creatingTimelineEvent = useSelector(GetCreatingOrEditingTimelineEvent);
  const isFileTCDisplayed = useAppSelector(GetIsFileTCDisplayed);

  //States
  const [searchMarkersPattern, SetSearchMarkersPatern] = React.useState<string>("");
  //const [markers, SetMarkersList] = React.useState<DbAssetMarkerInfos[]>([]);
  const [showTuto, setShowTuto] = React.useState<boolean>(false);
  const [filter, SetFilter] = React.useState<string[]>([
    DbMarkerCategory.Human.toString(),
    DbMarkerCategory.BatonAutoQc.toString(),
    DbMarkerCategory.HybrikAutoQc.toString(),
    DbMarkerSeverity.ERROR.toString(),
    DbMarkerSeverity.INFO.toString(),
    DbMarkerSeverity.WARNING.toString(),
  ]);
  const [hoverMarkerId, setHoverMarkerId] = useState<number>(-1);
  const [helpPopupAnchorEl, setHelpPopupAnchorEl] = React.useState<HTMLElement | null>(null);
  const [frameDuration, SetFrameDuration] = React.useState<number>(0);

  //Cache
  const platformMode = cacheService.GetPlatformMode();
  const isDrive = useMemo(
    () => platformMode === PlatformMode.Browser || asset?.classification === ObjectClass.Drive,
    [asset?.classification, platformMode]
  );

  // Events
  const SearchChange = (event) => {
    const {
      target: { value },
    } = event;
    if (event.target.id === "markers") SetSearchMarkersPatern(value.toLocaleLowerCase());
  };

  const FilterChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    SetFilter(typeof value === "string" ? value.split(",") : value);
  };

  const handleHelpClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (showTuto) {
      if (!setTutorialPage) return;

      setTutorialPage("player");

      e.stopPropagation();
      setHelpPopupAnchorEl(e.currentTarget);
    }
  };

  const FilterMarkers = (
    markers: QCMarkerCardProps[],
    categoryFilter: DbMarkerCategory[],
    SeverityFilter: DbMarkerSeverity[],
    isFileTCDisplayed: boolean,
    pattern?: string
  ) => {
    let result = markers.filter((x) => !(x.timecodes.in.markFrame === null || x.timecodes.in.markFrame < 0));
    if (categoryFilter !== null && categoryFilter.length > 0)
      result = result.filter((x) => categoryFilter.includes(x.tag!));
    if (SeverityFilter !== null && SeverityFilter.length > 0)
      result = result.filter((x) => SeverityFilter.includes(x.status!));
    if (pattern && pattern !== "" && pattern !== null) {
      let patternLower = pattern!.toLocaleLowerCase();
      result = result.filter(
        (x) =>
          x.desc?.toLocaleLowerCase().includes(patternLower) ||
          x.subtitle?.toLocaleLowerCase().includes(patternLower) ||
          x.tag?.toLocaleLowerCase().includes(patternLower)
      );
    }
    return result;
  };

  //Effects
  React.useEffect(() => {
    dispatch(
      SetFilteredTimelineMarkers(
        FilterMarkers(
          markers,
          filter.map((x) => DbMarkerCategory[x]),
          filter.map((x) => DbMarkerSeverity[x]),
          isFileTCDisplayed,
          searchMarkersPattern
        )
      )
    );
  }, [markers, filter, searchMarkersPattern]);

  useEffect(() => {
    dispatch(SetActiveTimeline(TimelineType.Markers));
  }, []);

  return (
    <Stack flex={1} overflow="hidden">
      {markers.length > 0 ? (
        <>
          <Stack
            py={2}
            px={2}
            flex={1}
            overflow="auto"
            sx={(theme) => ({
              ...theme.helpers(theme).scrollBar,
            })}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <OutlinedInput
                id="markers"
                label={t("common:standard")}
                size="small"
                placeholder={t("common:search")}
                value={searchMarkersPattern}
                onChange={SearchChange}
                endAdornment={<Search />}
                sx={{ flex: 1, flexShrink: 1, flexGrow: 1 }}
              />
              <Select
                labelId="filter-marker"
                size="small"
                id="filter-marker"
                displayEmpty
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                multiple
                renderValue={(selected) => <FilterList />}
                value={filter}
                onChange={FilterChange}
              >
                <ListSubheader>{t("common:category")}</ListSubheader>
                {Object.keys(DbMarkerCategory).map((x) => (
                  <MenuItem key={x} value={DbMarkerCategory[x]} selected={filter.includes(x) || false}>
                    <Checkbox
                      sx={{ padding: "0", paddingRight: "6px !important", height: "18px" }}
                      checked={filter.indexOf(x) > -1}
                    />
                    {x}
                  </MenuItem>
                ))}
                <ListSubheader>{t("common:severity")}</ListSubheader>
                {Object.keys(DbMarkerSeverity).map((x) => (
                  <MenuItem key={x} value={x} selected={filter.includes(x) || false}>
                    <Checkbox
                      sx={{ padding: "0", paddingRight: "6px !important", height: "18px" }}
                      checked={filter.indexOf(x) > -1}
                    />
                    {x}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack spacing={1} mt={1.5} flexWrap="wrap">
              {filteredMarkers.map((marker: QCMarkerCardProps, index) => (
                <QCMarkerCard
                  key={marker.id}
                  {...marker}
                  contentOnly={false}
                  deleteCallback={() => {
                    const markersTemp = [...markers];
                    markersTemp.splice(
                      markers.findIndex((x) => markers[index].id === x.id),
                      1
                    );
                    dispatch(SetTimelineMarkers(markersTemp));
                  }}
                />
              ))}
              {filteredMarkers.length === 0 && markers.length > 0 && (
                <Stack direction="column" justifyContent="center" alignItems="center" gap={1} pt={2}>
                  <Typography variant="body1" color="text.secondary">
                    {t("page:body.markersHidden", { count: markers.length - filteredMarkers.length })}
                  </Typography>
                  <Link onClick={(e) => SetFilter([])}>{t("button:clearFilters")}</Link>
                </Stack>
              )}
            </Stack>
          </Stack>
          <Box position="sticky" bottom={0} bgcolor="background.paper">
            <Divider />
            <Box flex={1} p={2}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="primary"
                startIcon={<PushPinOutlined />}
                onClick={(e) => {
                  dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }));
                }}
                disabled={!UserHasAcl(AclTypes.aclMarkerCreateOrUpdate) || creatingTimelineEvent}
              >
                {t("button:createAMarker")}
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <Stack p={2}>
          <EmptyListSlot
            title={t("page:emptyListSlot.title.noMarkers")}
            icon={<PushPinOutlined />}
            CTA={
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={(e) => {
                  dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }));
                }}
                disabled={!UserHasAcl(AclTypes.aclMarkerCreateOrUpdate) || creatingTimelineEvent}
              >
                {t("button:createAMarker")}
              </Button>
            }
          />
        </Stack>
      )}
    </Stack>
  );
};
